/**
 * AdminChatTab · v2.7.0 batch CW
 * ================================
 * Top-level Admin Console Chat panel. Three sections:
 *   1) Conversations list — incoming chats with unread/needs-human flags
 *   2) Open conversation viewer — see messages, reply as dispatch, resolve
 *   3) Settings panel — enable/disable, greeting, FAQ CRUD, offline window
 *
 * Renders inside the Admin Console `<Tabs>` at value="chat".
 */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  MessageCircle, Send, RefreshCw, Trash2, Plus, Bot, User, Settings,
  Phone, CheckCircle2, AlertCircle, Loader2, Palette, Shield,
} from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN = () => localStorage.getItem("rtg_admin_token") || "";
const headers = () => ({ Authorization: `Bearer ${TOKEN()}` });

function formatTime(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch (e) { return ts; }
}

export default function AdminChatTab() {
  const [section, setSection] = useState("inbox");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-amber-300 flex items-center gap-2">
          <MessageCircle className="h-5 w-5"/> Chat
        </h2>
      </div>
      <Tabs value={section} onValueChange={setSection}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="inbox" data-testid="chat-tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="settings" data-testid="chat-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="faq" data-testid="chat-tab-faq">FAQ</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-4"><ChatInbox/></TabsContent>
        <TabsContent value="settings" className="mt-4"><ChatSettings/></TabsContent>
        <TabsContent value="faq" className="mt-4"><ChatFaq/></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────── INBOX ──────────────────────────────────
function ChatInbox() {
  const [convos, setConvos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/admin/chatbot/conversations?limit=100${filter === "unread" ? "&only_unread=true" : ""}`, { headers: headers() });
      setConvos(data || []);
      // v2.7.5 Phase 4 · fire audio chime when an unread count grows.
      if (typeof window !== "undefined") {
        const unread = (data || []).reduce((s, c) => s + (Number(c.unread_count) || 0), 0);
        const prev = lastUnreadRef.current;
        if (prev != null && unread > prev) {
          try { window.dispatchEvent(new CustomEvent("rtg:chat_message", { detail: { delta: unread - prev }})); } catch { /* noop */ }
        }
        lastUnreadRef.current = unread;
      }
    } catch (err) { toast.error("Couldn't load conversations"); } finally { setLoading(false); }
  }, [filter]);

  // Tracks the previous total unread count between polls so we only fire the
  // chime on the transition (unread went UP).
  const lastUnreadRef = useRef(null);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const openConvo = async (id) => {
    setActiveId(id);
    try {
      const { data } = await axios.get(`${BASE}/api/admin/chatbot/conversations/${id}`, { headers: headers() });
      setDetail(data);
    } catch { toast.error("Couldn't open conversation"); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    try {
      await axios.post(`${BASE}/api/admin/chatbot/conversations/${activeId}/reply`,
        { message: reply },
        { headers: headers() });
      setReply("");
      openConvo(activeId);
      load();
      toast.success("Reply sent to visitor");
    } catch { toast.error("Reply failed"); }
  };

  const resolve = async () => {
    if (!activeId) return;
    try {
      await axios.post(`${BASE}/api/admin/chatbot/conversations/${activeId}/resolve`, {}, { headers: headers() });
      toast.success("Marked resolved");
      setActiveId(null);
      setDetail(null);
      load();
    } catch { toast.error("Resolve failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* List */}
      <Card className="bg-zinc-900 border-zinc-800 p-3 lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Conversations</div>
          <Button size="sm" variant="ghost" onClick={load} data-testid="chat-inbox-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
        </div>
        <div className="flex gap-1 mb-3">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} className="text-xs h-7" onClick={() => setFilter("all")} data-testid="chat-filter-all">All</Button>
          <Button size="sm" variant={filter === "unread" ? "default" : "outline"} className="text-xs h-7" onClick={() => setFilter("unread")} data-testid="chat-filter-unread">Unread</Button>
        </div>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto" data-testid="chat-conversation-list">
          {loading && <div className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin mx-auto text-zinc-500"/></div>}
          {!loading && convos.length === 0 && (
            <div className="text-center py-8 text-zinc-500 text-sm">No conversations yet.</div>
          )}
          {convos.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`chat-convo-${c.id.slice(0, 8)}`}
              onClick={() => openConvo(c.id)}
              className={`w-full text-left p-2 rounded-md border text-xs transition ${
                activeId === c.id
                  ? "bg-amber-400/10 border-amber-400/40"
                  : c.unread_admin
                    ? "bg-red-500/10 border-red-500/30 hover:bg-red-500/20"
                    : "bg-zinc-950 border-zinc-800 hover:border-zinc-700"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-bold text-zinc-200 truncate">{c.visitor_name || `Visitor ${c.id.slice(0, 6)}`}</span>
                {c.unread_admin && <span className="bg-red-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">NEW</span>}
                {c.needs_human && !c.unread_admin && <span className="bg-amber-500 text-zinc-900 text-[9px] px-1.5 py-0.5 rounded-full font-bold">HUMAN</span>}
              </div>
              <div className="text-zinc-400 truncate">{c.last_user_text || "—"}</div>
              <div className="text-zinc-500 text-[10px] mt-1">{formatTime(c.last_at)} · {c.message_count} msg{c.message_count !== 1 ? "s" : ""}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Detail */}
      <Card className="bg-zinc-900 border-zinc-800 p-3 lg:col-span-2 min-h-[60vh] flex flex-col" data-testid="chat-detail-pane">
        {!detail ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-zinc-800">
              <div>
                <div className="font-bold text-zinc-100">{detail.visitor_name || `Visitor ${detail.id.slice(0, 6)}`}</div>
                <div className="text-xs text-zinc-500">{detail.visitor_phone ? <a href={`tel:${detail.visitor_phone}`} className="hover:text-amber-400"><Phone className="inline h-3 w-3 mr-1"/>{detail.visitor_phone}</a> : "no phone provided"} · {detail.page_url || "—"}</div>
              </div>
              <Button size="sm" variant="outline" onClick={resolve} data-testid="chat-resolve" className="bg-emerald-900/30 border-emerald-700 hover:bg-emerald-900/50"><CheckCircle2 className="h-3.5 w-3.5 mr-1"/>Resolve</Button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 mb-3" data-testid="chat-messages-list">
              {(detail.messages || []).map((m, i) => (
                <div key={`${m.ts}-${i}`} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-zinc-800 text-zinc-100 border border-zinc-700"
                      : m.source === "admin"
                        ? "bg-emerald-900/40 border border-emerald-700/40 text-emerald-100"
                        : "bg-amber-400/15 border border-amber-400/30 text-amber-100"
                  }`}>
                    <div className="text-[9px] uppercase tracking-wider opacity-70 mb-1 font-bold flex items-center gap-1">
                      {m.role === "user" ? <><User className="h-3 w-3"/>visitor</> : m.source === "admin" ? <><User className="h-3 w-3"/>{m.admin_name || "dispatch"} (live)</> : <><Bot className="h-3 w-3"/>{m.source || "bot"}</>}
                      <span className="ml-auto opacity-60">{formatTime(m.ts).split(",")[1] || ""}</span>
                    </div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                placeholder="Reply as dispatch…"
                rows={2}
                data-testid="chat-admin-reply"
                className="bg-zinc-950 border-zinc-700 text-sm"
              />
              <Button onClick={sendReply} disabled={!reply.trim()} data-testid="chat-admin-send" className="bg-amber-400 hover:bg-amber-300 text-zinc-900"><Send className="h-4 w-4"/></Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

// ─────────────────────────────── SETTINGS ──────────────────────────────────
function ChatSettings() {
  const [cfg, setCfg] = useState(null);
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [shieldUploading, setShieldUploading] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/admin/chatbot/config`, { headers: headers() })
      .then((r) => {
        // Ensure theme object exists so the controls have something to bind to
        const data = r.data || {};
        if (!data.theme) data.theme = {};
        setCfg(data);
      })
      .catch(() => toast.error("Couldn't load config"));
  }, []);

  const setTheme = (patch) => setCfg((p) => ({ ...p, theme: { ...(p.theme || {}), ...patch } }));

  /**
   * v2.7.0 batch DB · Shield image upload pipeline.
   * Auto-scales the user's image to 64×64 (the retina size for the 32px
   * shield slot) using a canvas resize, then stores as a data URL inside
   * the config payload. This keeps the asset bundled with the config so
   * there's nothing extra to host or invalidate.
   */
  const handleShieldUpload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image too large (>4 MB)"); return;
    }
    setShieldUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => reject(new Error("read failed"));
        fr.readAsDataURL(file);
      });
      // Resize to 64×64 PNG via canvas (matches retina density of the 32px slot)
      const resized = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const SIZE = 64;
          const canvas = document.createElement("canvas");
          canvas.width = SIZE; canvas.height = SIZE;
          const ctx = canvas.getContext("2d");
          // Letterbox-fit while preserving aspect ratio
          const scale = Math.min(SIZE / img.width, SIZE / img.height);
          const w = img.width * scale, h = img.height * scale;
          const x = (SIZE - w) / 2, y = (SIZE - h) / 2;
          ctx.clearRect(0, 0, SIZE, SIZE);
          ctx.drawImage(img, x, y, w, h);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => reject(new Error("decode failed"));
        img.src = dataUrl;
      });
      setTheme({ shield_image_url: resized });
      toast.success("Shield image staged — click Save");
    } catch (err) {
      toast.error(`Image processing failed: ${err.message}`);
    } finally { setShieldUploading(false); }
  };

  const save = async () => {
    if (!pin) { toast.error("Enter admin PIN to save"); return; }
    setSaving(true);
    try {
      const payload = { ...cfg, pin };
      const { data } = await axios.put(`${BASE}/api/admin/chatbot/config`, payload, { headers: headers() });
      setCfg(data);
      toast.success("Saved · widget will refresh within 8 seconds");
      setPin("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 403) {
        toast.error("❌ Wrong admin PIN — try again");
      } else {
        toast.error(`Save failed: ${detail || "network error"}`);
      }
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-center py-12 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin mx-auto"/></div>;

  const theme = cfg.theme || {};

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-5 w-5 text-amber-400"/>
          <h3 className="font-bold text-zinc-100">Widget Settings</h3>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-zinc-400">Enable widget</span>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="chat-cfg-enabled"/>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Greeting</label>
        <Textarea value={cfg.greeting || ""} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} rows={2} data-testid="chat-cfg-greeting" className="bg-zinc-950 border-zinc-700"/>
      </div>

      {/* v2.7.0 batch DB · DISPATCH PHONE — single source of truth */}
      <div className="space-y-1">
        <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-2">
          <Phone className="h-3.5 w-3.5 text-amber-400"/>Dispatch Phone (Global)
        </label>
        <Input
          value={cfg.dispatch_phone || ""}
          onChange={(e) => setCfg({ ...cfg, dispatch_phone: e.target.value })}
          placeholder="(580) 681-0758"
          data-testid="chat-cfg-dispatch-phone"
          className="bg-zinc-950 border-zinc-700 font-mono"
        />
        <div className="text-[10px] text-zinc-500">
          Editing this updates the chat footer, fallback messages, AI prompt, and "Call Dispatch" quick button — everywhere — instantly.
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Launcher / Accent color</label>
        <ColorRow value={cfg.brand_color || "#fbbf24"} onChange={(v) => setCfg({ ...cfg, brand_color: v })} testid="chat-cfg-color"/>
      </div>

      {/* v2.7.0 batch DB · THEME — chat header / body / shield / footer */}
      <div className="border-t border-zinc-800 pt-4">
        <h4 className="text-sm font-bold text-zinc-200 mb-3 flex items-center gap-2"><Palette className="h-4 w-4 text-amber-400"/>Chat Appearance</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ColorField label="Header background"   value={theme.header_bg          || "#0c0d10"} onChange={(v) => setTheme({ header_bg: v })}          testid="chat-theme-header-bg"/>
          <ColorField label="Header text"         value={theme.header_text        || "#ffffff"} onChange={(v) => setTheme({ header_text: v })}        testid="chat-theme-header-text"/>
          <ColorField label="Chat background"     value={theme.body_bg            || "#111315"} onChange={(v) => setTheme({ body_bg: v })}            testid="chat-theme-body-bg"/>
          <ColorField label="Footer phone color"  value={theme.footer_phone_color || "#fbbf24"} onChange={(v) => setTheme({ footer_phone_color: v })} testid="chat-theme-footer-phone"/>
          <ColorField label="User bubble bg"      value={theme.user_bubble_bg     || "#fbbf24"} onChange={(v) => setTheme({ user_bubble_bg: v })}     testid="chat-theme-user-bubble-bg"/>
          <ColorField label="User bubble text"    value={theme.user_bubble_text   || "#18181b"} onChange={(v) => setTheme({ user_bubble_text: v })}   testid="chat-theme-user-bubble-text"/>
        </div>
      </div>

      {/* SHIELD */}
      <div className="border-t border-zinc-800 pt-4">
        <h4 className="text-sm font-bold text-zinc-200 mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-amber-400"/>Chat Shield</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Shield color (used if no image)</label>
            <ColorRow value={theme.shield_color || "#fbbf24"} onChange={(v) => setTheme({ shield_color: v })} testid="chat-theme-shield-color"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Shield image (auto-resized to 64×64)</label>
            <div className="flex items-center gap-2 mt-1">
              {theme.shield_image_url ? (
                <img src={theme.shield_image_url} alt="Shield preview" className="w-10 h-10 rounded-full object-cover border border-zinc-700" data-testid="chat-theme-shield-preview"/>
              ) : (
                <div className="w-10 h-10 rounded-full flex items-center justify-center border border-zinc-700" style={{ background: theme.shield_color || "#fbbf24" }}>
                  <MessageCircle className="h-4 w-4 text-zinc-900"/>
                </div>
              )}
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(e) => handleShieldUpload(e.target.files?.[0])}
                disabled={shieldUploading}
                data-testid="chat-theme-shield-upload"
                className="bg-zinc-950 border-zinc-700 text-xs file:text-amber-400 file:bg-zinc-900 file:border-zinc-700 file:mr-3"
              />
              {theme.shield_image_url && (
                <Button size="sm" variant="outline" onClick={() => setTheme({ shield_image_url: "" })} data-testid="chat-theme-shield-clear" className="h-8 text-xs">Clear</Button>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 mt-1">PNG/JPG/WebP/SVG · max 4 MB · auto-cropped square at 64×64 retina.</div>
          </div>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">System prompt (AI fallback)</label>
        <Textarea value={cfg.system_prompt || ""} onChange={(e) => setCfg({ ...cfg, system_prompt: e.target.value })} rows={5} data-testid="chat-cfg-prompt" className="bg-zinc-950 border-zinc-700 font-mono text-xs"/>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h4 className="text-sm font-bold text-zinc-200 mb-2 flex items-center gap-2"><AlertCircle className="h-4 w-4 text-amber-400"/>After-hours offline window</h4>
        <div className="flex items-center gap-3 mb-3">
          <Switch checked={!!cfg.offline_after_midnight} onCheckedChange={(v) => setCfg({ ...cfg, offline_after_midnight: v })} data-testid="chat-cfg-offline-toggle"/>
          <span className="text-sm text-zinc-300">Enable offline window</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Start hour CT (0-23)</label>
            <Input type="number" min={0} max={23} value={cfg.offline_start_hour_ct ?? 0} onChange={(e) => setCfg({ ...cfg, offline_start_hour_ct: parseInt(e.target.value) || 0 })} data-testid="chat-cfg-offline-start" className="bg-zinc-950 border-zinc-700"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">End hour CT (0-23)</label>
            <Input type="number" min={0} max={23} value={cfg.offline_end_hour_ct ?? 6} onChange={(e) => setCfg({ ...cfg, offline_end_hour_ct: parseInt(e.target.value) || 6 })} data-testid="chat-cfg-offline-end" className="bg-zinc-950 border-zinc-700"/>
          </div>
        </div>
        <div className="mt-2">
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Offline message</label>
          <Textarea value={cfg.offline_message || ""} onChange={(e) => setCfg({ ...cfg, offline_message: e.target.value })} rows={2} data-testid="chat-cfg-offline-msg" className="bg-zinc-950 border-zinc-700"/>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4 flex items-end gap-2">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Admin PIN to save</label>
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="chat-cfg-pin" className="bg-zinc-950 border-zinc-700" placeholder="••••••"/>
        </div>
        <Button onClick={save} disabled={saving || !pin} data-testid="chat-cfg-save" className="bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold">{saving ? "Saving…" : "Save"}</Button>
      </div>
    </Card>
  );
}

// Inline labelled color row (large swatch + native picker + hex input)
function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</label>
      <ColorRow value={value} onChange={onChange} testid={testid}/>
    </div>
  );
}

function ColorRow({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testid}-picker`}
        className="w-9 h-9 rounded border border-zinc-700 bg-transparent cursor-pointer flex-shrink-0"
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-zinc-950 border-zinc-700 font-mono text-xs h-9 max-w-[110px]"
      />
    </div>
  );
}

// ─────────────────────────────── FAQ ──────────────────────────────────
function ChatFaq() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ question: "", answer: "", keywords: "" });
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/admin/chatbot/faq`, { headers: headers() });
      setList(data || []);
      // Auto-seed defaults if empty
      if ((data || []).length === 0) {
        await axios.post(`${BASE}/api/admin/chatbot/seed-defaults`, {}, { headers: headers() });
        const { data: d2 } = await axios.get(`${BASE}/api/admin/chatbot/faq`, { headers: headers() });
        setList(d2 || []);
      }
    } catch { toast.error("Couldn't load FAQ"); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) return;
    const body = {
      question: draft.question,
      answer: draft.answer,
      keywords: draft.keywords.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await axios.put(`${BASE}/api/admin/chatbot/faq/${editingId}`, body, { headers: headers() });
      } else {
        await axios.post(`${BASE}/api/admin/chatbot/faq`, body, { headers: headers() });
      }
      setDraft({ question: "", answer: "", keywords: "" });
      setEditingId(null);
      load();
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ entry?")) return;
    try {
      await axios.delete(`${BASE}/api/admin/chatbot/faq/${id}`, { headers: headers() });
      load();
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const edit = (entry) => {
    setDraft({ question: entry.question, answer: entry.answer, keywords: (entry.keywords || []).join(", ") });
    setEditingId(entry.id);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3">
        <h3 className="font-bold text-zinc-100 flex items-center gap-2"><Plus className="h-4 w-4"/> {editingId ? "Edit FAQ entry" : "Add FAQ entry"}</h3>
        <Input placeholder="Question" value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} data-testid="faq-question" className="bg-zinc-950 border-zinc-700"/>
        <Textarea placeholder="Answer" value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={4} data-testid="faq-answer" className="bg-zinc-950 border-zinc-700"/>
        <Input placeholder="Keywords (comma-separated)" value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} data-testid="faq-keywords" className="bg-zinc-950 border-zinc-700"/>
        <div className="flex gap-2">
          <Button onClick={save} data-testid="faq-save" className="bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold">{editingId ? "Update" : "Add"}</Button>
          {editingId && <Button variant="outline" onClick={() => { setEditingId(null); setDraft({ question: "", answer: "", keywords: "" }); }}>Cancel</Button>}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <h3 className="font-bold text-zinc-100 mb-3">FAQ entries ({list.length})</h3>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-500"/>}
        <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="faq-list">
          {list.map((entry) => (
            <div key={entry.id} className="p-3 rounded-md bg-zinc-950 border border-zinc-800">
              <div className="font-bold text-zinc-100 text-sm mb-1">{entry.question}</div>
              <div className="text-xs text-zinc-400 mb-2 whitespace-pre-wrap">{entry.answer}</div>
              {(entry.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {entry.keywords.map((k, i) => <span key={`${entry.id}-kw-${i}`} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{k}</span>)}
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => edit(entry)} data-testid={`faq-edit-${entry.id}`} className="h-7 text-xs">Edit</Button>
                <Button size="sm" variant="outline" onClick={() => remove(entry.id)} data-testid={`faq-delete-${entry.id}`} className="h-7 text-xs text-red-400 border-red-900/40 hover:bg-red-900/20"><Trash2 className="h-3 w-3"/></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
